import { defineStore } from "pinia";
import type { WebModelGetHardwareType } from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  addHardwareType,
  findAllHardwareTypes,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";
import { sortBy } from "~/helpers/sortBy";

interface State {
  hardwareTypes: WebModelGetHardwareType[];
}

export const useHardwareTypesStore = defineStore("hardwareType", {
  // convert to a function
  state: (): State => ({
    hardwareTypes: [], // initial state
  }),
  getters: {
    getHardwareTypesForSelectField(): { value: number; text: string }[] {
      return this.hardwareTypes
        .map((hardwareType) => ({
          value: hardwareType.id,
          text: hardwareType.title ?? "",
        }))
        .filter(
          (item): item is { value: number; text: string } =>
            item.value !== undefined && item.text !== "",
        )
        .sort(sortBy({ key: "text", order: "asc" }));
    },
    getHardwareTypesWithoutTitleForSelectField(): {
      value: number;
      text: string;
    }[] {
      return this.hardwareTypes
        .map((hardwareType) => ({
          value: hardwareType.id,
          text: hardwareType.targetType ?? "",
        }))
        .filter(
          (item): item is { value: number; text: string } =>
            item.value !== undefined && item.text !== "",
        )
        .sort(sortBy({ key: "text", order: "asc" }));
    },
    // add getter here to get a hardwareType by .targetType
    getHardwareTypeByTargetType:
      (state) =>
      (targetType: string): WebModelGetHardwareType | undefined => {
        return state.hardwareTypes.find(
          (hardwareType) => hardwareType.targetType === targetType,
        );
      },
  },
  actions: {
    async loadHardwareTypes() {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(findAllHardwareTypes())),
      );
      console.debug("loadHardwareTypes: ", data, error);
      if (data) {
        this.hardwareTypes = data;
      }
    },

    // admin only
    async addHardwareType(
      targetType: string,
      title: string,
      url: string,
      manufacturer?: string,
    ) {
      const { data } = await withErrorMessage(
        withLoading(addHardwareType({ targetType, title, url, manufacturer })),
      );
      console.debug("addHardwareType: ", data);
      if (data) {
        await this.loadHardwareTypes();
      }
    },

    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
